<template>
  <van-dialog 
    v-model:show="show" 
    class="ob-dialog"
    :close-on-click-overlay="true"
    :show-confirm-button="true"
    :show-cancel-button="true"
    :width="width"
    :zIndex="zIndex"
  >
    <template #title>
      <slot name="title">
        <div class="ob-dialog-title_wrapper" v-if="title"
          :style="{
            background
          }"
        >
          <p class="title"
            :style="{
              textAlign 
            }"
          >{{ title }}</p>
          <SvgIcon v-if="showCancelIcon" name="icon-close" @click="closeDialog"/>
        </div>
      </slot>
    </template>
    <slot>
      <p v-if="content" class="content" v-html="content"></p>
    </slot>
    <template #footer>
      <slot name="footer" v-if="footer">
        <div class="ob-dialog-footer" v-if="isWeb">
          <ObButton 
            v-if="showCancelButton"
            class="ob-dialog-cancel__button"
            @click="onCancel"
          >
            {{ translate ? $t(cancelText) : cancelText }}
          </ObButton>
          <ObButton 
            v-if="showConfirmButton"
            @click="onConfirm"
          >
            {{ translate ? $t(confirmText) : confirmText }}
          </ObButton>
        </div>
        <div class="button-group" v-else>
          <span v-if="showCancelButton" @click="onCancel" >{{ translate ? $t(cancelText) : cancelText }}</span>
          <span v-if="showConfirmButton" @click="onConfirm">{{ translate ? $t(confirmText) : confirmText }}</span>
        </div>
      </slot>
    </template>
  </van-dialog>
</template>

<script>
import { useSystemStore } from '@/stores/system'
import { storeToRefs } from 'pinia'

export default {
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    content: {
      type: String,
      default: ''
    },
    textAlign: {
      type: String,
      default: 'center'
    },
    width: {
      type: [String, Number],
      default: 'fit-content'
    },
    footer: {
      type: Boolean,
      default: false
    },
    translate: {
      type: Boolean,
      default: true,
    },
    showConfirmButton: {
      type: Boolean,
      default: true,
    },
    showCancelIcon: {
      type: Boolean,
      default: true,
    },
    confirmText: {
      type: String, 
      default: 'yes'
    },
    showCancelButton: {
      type: Boolean,
      default: true,
    },
    cancelText: {
      type: String,
      default: 'cancel'
    },
    background: {
      type: String,
      default: ''
    },
    zIndex: {
      type: [String, Number]
    }
  },
  emits: ['update:visible', 'confirmCallback', 'cancelCallback'],
  setup (props, { emit }) {

    const systemStore = useSystemStore()
    const { isWeb } = storeToRefs(systemStore)

    const { visible } = toRefs(props)

    const show = ref(false)

    const onConfirm = () => {
      emit('confirmCallback', show)
    }

    const onCancel = () => {
      closeDialog()
      emit('cancelCallback')
    }

    function closeDialog () {
      show.value = false
    }


    watchEffect(() => {
      if (!show.value) {
        emit('update:visible', show.value)
      }
    })

    watch(visible, (val) => {
      show.value = val
    }, {
      immediate: true
    })

    

    return {
      show,
      isWeb,
      onConfirm,
      onCancel,
      closeDialog
    }
  }
}
</script>

<style lang="postcss">
  
  .ob-dialog {
    --van-dialog-header-isolated-padding: 0;
    --van-dialog-radius: var(--radius-xl);
    .content {
      padding: 24px 40px 0;
      font-size: 18px;
      line-height: 26px;
      text-align: center;
      word-break: break-word;
    }
  }
  .ob-dialog-title_wrapper {
    position: relative;
    height: auto;
    .title {
      font-size: 24px;
      font-family: 'orbit-M';
      padding: 40px 64px 0;
      word-break: break-word;
      line-height: 32px;
    }
    .icon-close {
      position: absolute;
      top: 14px;
      right: 14px;
      width: 28px;
      height: 28px;
    }
  }
  .ob-dialog-footer {
    display: flex;
    align-items: center;
    justify-content: space-around;
    width: 100%;
    padding: 32px 40px;
    box-sizing: border-box;
    .ob-button {
      flex: 1;
    }
    .ob-dialog-cancel__button {
      color: var(--color-text-primary);
      background-color: var(--color-background-primary);
    }
  }

  @media screen and (min-width: 1024px) {
    .ob-dialog {
      top: 50%;
    }
  }

  @media screen and (max-width: 1024px) {
    .ob-dialog {
      .title {
        font-size: 18px;
        line-height: 26px;
        padding: 24px 24px 6px;
      }
      .content {
        font-size: 14px;
        line-height: 20px;
        padding: 24px 18px;
      }
      .button-group {
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;
        height: 58px;
        padding: 12px 0 22px;
        box-sizing: border-box;
        span {
          position: relative;
          width: 50%;
          display: flex;
          flex-flow: row nowrap;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: var(--theme);
          &:first-child::after {
            content: "";
            position: absolute;
            right: 0;
            width: .66px;
            height: 22px;
            background: var(--line);
          }
        }
      }
    }
  }
</style>